/*
 * نظام إدارة الصيانة الشامل
 * حقوق الملكية © 2025 Ahmed Elswah
 * الإصدار: 4.0
 * تاريخ التحديث: 7/4/2025
 */

import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from './config/firebase';
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { UserSettingsProvider } from './contexts/UserSettingsContext';
import { CartProvider } from './contexts/CartContext';
import { StoreSettingsProvider } from './contexts/StoreSettingsContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import ThemeManager from './components/ThemeManager';
import Layout from './components/Layout';
import { StoreLayout } from './components/store';
import NewStoreLayout from './components/store/NewStoreLayout';
import { User, UserRole } from './utils/types';
import AdminProtectedRoute from './components/AdminProtectedRoute';

// Importar páginas
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import TechniciansPage from './pages/TechniciansPage';
import TechnicianReportsPage from './pages/TechnicianReportsPage';
import TechnicianDeviceReportPage from './pages/TechnicianDeviceReportPage';
import MaintenanceRecordsPage from './pages/MaintenanceRecordsPage';
import MaintenanceDetailsPage from './pages/MaintenanceDetailsPage';
import MaintenanceCreatePage from './pages/MaintenanceCreatePage';
import InventoryPage from './pages/InventoryPage';
import NewAddInventoryPage from './pages/NewAddInventoryPage';
import NewEditInventoryPage from './pages/NewEditInventoryPage';
import InventoryDetailsPage from './pages/InventoryDetailsPage';
import WarehousePage from './pages/WarehousePage';
import CategoryPage from './pages/CategoryPage';
import POSPage from './pages/POSPage';
import ReportsPage from './pages/ReportsPage';
import MonthlyReportPage from './pages/MonthlyReportPage';
import MaintenanceBankPage from './pages/MaintenanceBankPage';
import TopicViewPage from './pages/maintenanceBank/TopicViewPage';
import TopicEditPage from './pages/maintenanceBank/TopicEditPage';
import ImageUploadTest from './components/ImageUploadTest';
import DrawerDiagnostics from './components/DrawerDiagnostics';
import MaintenanceFlowExample from './components/MaintenanceFlowExample';
import BranchesPage from './pages/BranchesPage';
import SettingsPage from './pages/SettingsPage';
import AdvancedSettingsPage from './pages/AdvancedSettingsPage';
import WhatsAppMessagingPage from './pages/WhatsAppMessagingPage';
import WhatsAppBaileysPage from './pages/WhatsAppBaileysPage';
import RegisterUserPage from './pages/RegisterUserPage';
import AdminToolsPage from './pages/AdminToolsPage';
import PartsCostFixPage from './pages/admin/PartsCostFixPage';
import BackupPage from './pages/BackupPage';
import AdminProfilePage from './pages/ProfilePage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import UnifiedDebtsPage from './pages/UnifiedDebtsPage';
import DebugPermissionsPage from './pages/DebugPermissionsPage';
import ExpensesPage from './pages/ExpensesPage';
import SalesPage from './pages/SalesPage';
import InvoicesPage from './pages/InvoicesPage';
import ActivityLogPageSimple from './pages/ActivityLogPageSimple';
import TeamCommunicationPage from './pages/TeamCommunicationPage';

// صفحات المتجر الإلكتروني
import StorePage from './pages/store/StorePage';
import ProductDetailPage from './pages/store/ProductDetailPage';
import NewProductDetailPage from './pages/store/NewProductDetailPage';
import CategoryProductsPage from './pages/store/CategoryProductsPage';
import CategoryDetailsPage from './pages/store/CategoryDetailsPage';
import CategoriesPage from './pages/store/CategoriesPage';
import OffersPage from './pages/store/OffersPage';
import StaticPage from './pages/store/StaticPage';
import CheckoutPage from './pages/store/CheckoutPage';
import StoreSettingsPage from './pages/store/StoreSettingsPage';
import ProductSuggestionsPage from './pages/store/ProductSuggestionsPage';
import ThemeDemoPage from './pages/store/ThemeDemoPage';
import ProfilePage from './pages/store/ProfilePage';
import OrderDetailPage from './pages/store/OrderDetailPage';
import SearchResultsPage from './pages/store/SearchResultsPage';
import ProductTypePage from './pages/store/ProductTypePage';
import TrackingPage from './pages/store/TrackingPage';
import OrdersManagementPage from './pages/admin/OrdersManagementPage';
import PagesManagementPage from './pages/admin/PagesManagementPage';
import PromotionsManagementPage from './pages/admin/PromotionsManagementPage';
import PerformanceOptimizer from './components/common/PerformanceOptimizer';
import CurrencyTestPage from './components/settings/CurrencyTestPage';
import MobileOptimizedStoreLayout from './components/store/MobileOptimizedStoreLayout';
import MobileOptimizedStorePage from './pages/store/MobileOptimizedStorePage';
import MobileOptimizedProductDetailPage from './pages/store/MobileOptimizedProductDetailPage';
import NotFoundPage from './pages/NotFoundPage';
import DataProtectionPage from './pages/DataProtectionPage';
import DropboxSettingsPage from './pages/DropboxSettingsPage';
import FirebaseUsagePage from './pages/admin/FirebaseUsagePage';
import ImageUploadSettingsPage from './pages/maintenanceBank/ImageUploadSettingsPage';
import UnifiedImageSettingsPage from './pages/UnifiedImageSettingsPage';
import QuickSetupPage from './pages/QuickSetupPage';
import InitialSetupChecker from './components/InitialSetupChecker';
import DebtPaymentDebugger from './components/debug/DebtPaymentDebugger';

// استيراد نظام الحماية
import { startFullProtection } from './utils/dataProtection';

// استيراد خدمة المجدول
import { startScheduler, stopScheduler } from './services/schedulerService';

// استيراد خدمة فحص الإعداد الأولي
import { needsInitialSetup } from './services/setupService';



// تم نقل إنشاء السمة إلى مكون ThemeManager

function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('Setting up auth state listener...');

    // تفعيل نظام الحماية من القيم الافتراضية
    const stopProtection = startFullProtection();

    // بدء تشغيل المجدول للمهام التلقائية
    startScheduler();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('Auth state changed:', firebaseUser ? `User ${firebaseUser.uid} signed in` : 'User signed out');

      if (firebaseUser) {
        try {
          // Fetch user data from Firestore
          console.log('Fetching user data from Firestore for user:', firebaseUser.uid);
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            // User document exists in Firestore
            console.log('User document found in Firestore');
            const userData = userDoc.data() as User;

            // التأكد من أن البيانات تحتوي على جميع الحقول المطلوبة
            const { getDefaultPermissions } = require('./services/permissionService');
            const defaultPermissions = getDefaultPermissions(userData.role || UserRole.ADMIN);

            const completeUserData: User = {
              id: firebaseUser.uid,
              email: firebaseUser.email || userData.email || '',
              name: firebaseUser.displayName || userData.name || '',
              role: userData.role || UserRole.ADMIN,
              branchId: userData.branchId || '',
              branchName: userData.branchName || '',
              allowedWarehouses: userData.allowedWarehouses || [],
              isActive: userData.isActive !== undefined ? userData.isActive : true,
              permissions: userData.permissions || defaultPermissions,
              createdAt: userData.createdAt || new Date(),
              updatedAt: new Date()
            };

            console.log('User data loaded successfully:', completeUserData);
            
            // إذا لم يكن هناك مخازن مسموح بها، محاولة إصلاحها تلقائياً
            if ((!completeUserData.allowedWarehouses || completeUserData.allowedWarehouses.length === 0) && completeUserData.branchId) {
              console.log('🔧 No allowedWarehouses found, attempting to fix...');
              try {
                const { updateUserWarehousesFromBranch } = await import('./services/userService');
                await updateUserWarehousesFromBranch(completeUserData.id);
                
                // إعادة تحميل بيانات المستخدم بعد الإصلاح
                const updatedUserDoc = await getDoc(userDocRef);
                if (updatedUserDoc.exists()) {
                  const updatedUserData = updatedUserDoc.data() as User;
                  completeUserData.allowedWarehouses = updatedUserData.allowedWarehouses || [];
                  console.log('✅ Fixed allowedWarehouses:', completeUserData.allowedWarehouses);
                }
              } catch (fixError) {
                console.error('Error fixing warehouse permissions:', fixError);
              }
            }
            
            setCurrentUser(completeUserData);
          } else {
            // مستند المستخدم غير موجود في Firestore، إنشاء واحد افتراضي
            console.log('User document not found in Firestore, using default values');
            const { getDefaultPermissions } = require('./services/permissionService');
            const defaultPermissions = getDefaultPermissions(UserRole.ADMIN);

            const defaultUserData: User = {
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              name: firebaseUser.displayName || '',
              role: UserRole.ADMIN,
              branchId: '',
              isActive: true,
              permissions: defaultPermissions,
              createdAt: new Date(),
              updatedAt: new Date()
            };

            // Store default user data in Firestore for future use
            try {
              await setDoc(doc(db, 'users', firebaseUser.uid), defaultUserData);
              console.log('Created new user document in Firestore');
            } catch (docCreateError) {
              console.error('Error creating user document:', docCreateError);
            }

            setCurrentUser(defaultUserData);
          }
        } catch (error) {
          console.error('Error getting user data from Firestore:', error);
          // الرجوع إلى بيانات المستخدم الأساسية من Firebase Auth
          const { getDefaultPermissions } = require('./services/permissionService');
          const defaultPermissions = getDefaultPermissions(UserRole.ADMIN);

          const basicUserData: User = {
            id: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || '',
            role: UserRole.ADMIN,
            branchId: '',
            isActive: true,
            permissions: defaultPermissions,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          setCurrentUser(basicUserData);
        }
      } else {
        console.log('No user is signed in');
        setCurrentUser(null);
      }

      setLoading(false);
    });

    return () => {
      console.log('Cleaning up auth state listener');
      unsubscribe();
      // إيقاف نظام الحماية عند إلغاء التحميل
      stopProtection();
      // إيقاف المجدول عند إلغاء التحميل
      stopScheduler();
    };
  }, []);

  // شاشة التحميل مع كرات ملونة
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'Cairo, sans-serif',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
      }}>
        {/* دائرة التحميل */}
        <div style={{
          width: '90px',
          height: '90px',
          border: '4px solid rgba(0, 0, 0, 0.1)',
          borderTop: '4px solid #1976d2',
          borderRight: '4px solid #ff9800',
          borderBottom: '4px solid #4caf50',
          borderRadius: '50%',
          animation: 'spin 1.2s linear infinite',
          marginBottom: '50px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
        }} />

        {/* الكرات الملونة */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
          <div style={{
            width: '25px',
            height: '25px',
            backgroundColor: '#1976d2', // أزرق
            borderRadius: '50%',
            animation: 'bounce 1.4s ease infinite',
            boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)'
          }} />
          <div style={{
            width: '25px',
            height: '25px',
            backgroundColor: '#ff9800', // برتقالي
            borderRadius: '50%',
            animation: 'bounce 1.4s ease infinite 0.2s',
            boxShadow: '0 4px 8px rgba(255, 152, 0, 0.4)'
          }} />
          <div style={{
            width: '25px',
            height: '25px',
            backgroundColor: '#4caf50', // أخضر
            borderRadius: '50%',
            animation: 'bounce 1.4s ease infinite 0.4s',
            boxShadow: '0 4px 8px rgba(76, 175, 80, 0.4)'
          }} />
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-15px); }
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <Router>
      <ThemeManager>
        <SettingsProvider>
          <UserSettingsProvider>
            <CurrencyProvider>
              <AuthProvider value={{
                currentUser: currentUser,
                setCurrentUser: (user: User | null) => setCurrentUser(user)
              }}>
                <StoreSettingsProvider>
                  <CartProvider>
                    <InitialSetupChecker>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterUserPage />} />
                  <Route path="/setup" element={<QuickSetupPage />} />

                  {/* المتجر الإلكتروني كصفحة رئيسية */}
                  <Route path="/" element={<StoreLayout />}>
                    <Route index element={<StorePage />} />
                    <Route path="product/:productId" element={<ProductDetailPage />} />
                    <Route path="categories" element={<CategoriesPage />} />
                    <Route path="category/:categoryId" element={<CategoryDetailsPage />} />
                    <Route path="category/:categoryId/products" element={<CategoryProductsPage />} />
                    <Route path="category/:categoryId/:subcategoryId" element={<CategoryProductsPage />} />
                    <Route path="offers" element={<OffersPage />} />
                    <Route path="checkout" element={<CheckoutPage />} />
                    <Route path="theme-demo" element={<ThemeDemoPage />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="order/:orderId" element={<OrderDetailPage />} />
                    <Route path="search" element={<SearchResultsPage />} />
                    <Route path="products/:type" element={<ProductTypePage />} />
                    <Route path="tracking" element={<TrackingPage />} />
                    <Route path="tracking/:trackingNumber" element={<TrackingPage />} />
                    {/* مسارات الصفحات الثابتة */}
                    <Route path=":slug" element={<StaticPage />} />
                  </Route>

                  {/* المتجر الإلكتروني بالتصميم الجديد */}
                  <Route path="/store-new" element={<NewStoreLayout />}>
                    <Route index element={<StorePage />} />
                    <Route path="product/:productId" element={<NewProductDetailPage />} />
                    <Route path="categories" element={<CategoriesPage />} />
                    <Route path="category/:categoryId" element={<CategoryDetailsPage />} />
                    <Route path="category/:categoryId/products" element={<CategoryProductsPage />} />
                    <Route path="category/:categoryId/:subcategoryId" element={<CategoryProductsPage />} />
                    <Route path="offers" element={<OffersPage />} />
                    <Route path="checkout" element={<CheckoutPage />} />
                    <Route path="theme-demo" element={<ThemeDemoPage />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="order/:orderId" element={<OrderDetailPage />} />
                    <Route path="search" element={<SearchResultsPage />} />
                    <Route path="tracking" element={<TrackingPage />} />
                    <Route path="tracking/:trackingNumber" element={<TrackingPage />} />
                    {/* مسارات الصفحات الثابتة */}
                    <Route path=":slug" element={<StaticPage />} />
                  </Route>

                  {/* المتجر المحسن للهاتف */}
                  <Route path="/mobile-store" element={<MobileOptimizedStoreLayout />}>
                    <Route index element={<MobileOptimizedStorePage />} />
                    <Route path="product/:productId" element={<MobileOptimizedProductDetailPage />} />
                    <Route path="categories" element={<CategoriesPage />} />
                    <Route path="category/:categoryId" element={<CategoryDetailsPage />} />
                    <Route path="category/:categoryId/products" element={<CategoryProductsPage />} />
                    <Route path="checkout" element={<CheckoutPage />} />
                    <Route path="order/:orderId" element={<OrderDetailPage />} />
                    <Route path="offers" element={<OffersPage />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="search" element={<SearchResultsPage />} />
                    <Route path="tracking" element={<TrackingPage />} />
                    <Route path="tracking/:trackingNumber" element={<TrackingPage />} />
                  </Route>

                  {/* لوحة التحكم - حماية من وصول العملاء */}
                  <Route path="/admin" element={
                    <AdminProtectedRoute>
                      <Layout />
                    </AdminProtectedRoute>
                  }>
                    <Route index element={<DashboardPage />} />
                    <Route path="customers" element={<CustomersPage />} />
                    <Route path="technicians" element={<TechniciansPage />} />
                    <Route path="maintenance-records" element={<MaintenanceRecordsPage />} />
                    <Route path="maintenance/create" element={<MaintenanceCreatePage />} />
                    <Route path="maintenance/:id" element={<MaintenanceDetailsPage />} />
                    <Route path="inventory" element={<InventoryPage />} />
                    <Route path="inventory/add" element={<NewAddInventoryPage />} />
                    <Route path="inventory/edit/:id" element={<NewEditInventoryPage />} />
                    <Route path="inventory/:id" element={<InventoryDetailsPage />} />
                    <Route path="warehouse" element={<WarehousePage />} />
                    <Route path="category" element={<CategoryPage />} />
                    <Route path="pos" element={<POSPage />} />
                    <Route path="sales" element={<SalesPage />} />
                    <Route path="invoices" element={<InvoicesPage />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="monthly-report" element={<MonthlyReportPage />} />
                    <Route path="maintenance-bank" element={<MaintenanceBankPage />} />
                    <Route path="maintenance-bank/topic/:topicId" element={<TopicViewPage />} />
                    <Route path="maintenance-bank/topic/:topicId/edit" element={<TopicEditPage />} />
                    <Route path="image-upload-test" element={<ImageUploadTest />} />
                    <Route path="drawer-diagnostics" element={<DrawerDiagnostics />} />
                    <Route path="maintenance-flow-example" element={<MaintenanceFlowExample />} />
                    <Route path="technician-reports" element={<TechnicianReportsPage />} />
                    <Route path="technician-device-report" element={<TechnicianDeviceReportPage />} />
                    <Route path="branches" element={<BranchesPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="advanced-settings" element={<AdvancedSettingsPage />} />
                    <Route path="whatsapp" element={<WhatsAppMessagingPage />} />
                    <Route path="whatsapp-baileys" element={<WhatsAppBaileysPage />} />
                    <Route path="admin-tools" element={<AdminToolsPage />} />
                    <Route path="parts-cost-fix" element={<PartsCostFixPage />} />
                    <Route path="backup" element={<BackupPage />} />
                    <Route path="profile" element={<AdminProfilePage />} />
                    <Route path="account-settings" element={<AccountSettingsPage />} />
                    <Route path="debts" element={<UnifiedDebtsPage />} />
                    <Route path="expenses" element={<ExpensesPage />} />
                    <Route path="debug-permissions" element={<DebugPermissionsPage />} />
                    <Route path="store-settings" element={<StoreSettingsPage />} />
                    <Route path="product-suggestions" element={<ProductSuggestionsPage />} />
                    <Route path="pages" element={<PagesManagementPage />} />
                    <Route path="promotions" element={<PromotionsManagementPage />} />
                    <Route path="orders" element={<OrdersManagementPage />} />
                    <Route path="data-protection" element={<DataProtectionPage />} />
                    <Route path="dropbox" element={<DropboxSettingsPage />} />
                    <Route path="firebase-usage" element={<FirebaseUsagePage />} />
                    <Route path="performance" element={<PerformanceOptimizer />} />
                    <Route path="currency-test" element={<CurrencyTestPage />} />
                    <Route path="activity-log" element={<ActivityLogPageSimple />} />
                    <Route path="team" element={<TeamCommunicationPage />} />
                    <Route path="image-upload-settings" element={<ImageUploadSettingsPage />} />
                    <Route path="unified-image-settings" element={<UnifiedImageSettingsPage />} />
                    <Route path="debt-payment-debug" element={<DebtPaymentDebugger />} />
                    <Route path="*" element={<Navigate to="/admin" replace />} />
                  </Route>

              {/* إعدادات المتجر الإلكتروني - تم نقلها إلى داخل مسار /admin */}

              {/* صفحة 404 - عند عدم وجود المسار */}
              <Route path="*" element={<NotFoundPage />} />
                </Routes>
                    </InitialSetupChecker>
                </CartProvider>
              </StoreSettingsProvider>
            </AuthProvider>
          </CurrencyProvider>
          </UserSettingsProvider>
        </SettingsProvider>
      </ThemeManager>
    </Router>
  );
}

export default App;
